import { useCallback, useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Text,
  Textarea,
  VStack,
  type BoxProps,
} from '@chakra-ui/react'
import { MessageSquare, Pencil, ArrowRight, Wrench } from 'lucide-react'
import type { TicketUpdate } from '@/types/parentTicket'
import { TicketService } from '@/lib/services/ticket'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAuthStore } from '@/store/auth'
import CommentComposer from './CommentComposer'
import {
  buildUnifiedTimeline,
  countThreadNodes,
  isCommentType,
  MAX_THREAD_DEPTH,
  normalizeUpdate,
  buildCommentTree,
} from './buildCommentTree'

const MAX_CHARS = 2000
const THREAD_INDENT = 28

type Props = {
  ticketId: string
  updates: TicketUpdate[]
  onRefresh: () => void | Promise<void>
  onEdit?: (update: TicketUpdate) => void
  embedded?: boolean
} & BoxProps

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

function authorName(u: TicketUpdate) {
  return u.author?.full_name?.trim() || 'Agent'
}

function authorInitial(name: string) {
  return (name.trim()[0] ?? '?').toUpperCase()
}

type ThreadCommentProps = {
  node: TicketUpdate
  depth: number
  ticketId: string
  currentUserId: number | null
  onRefresh: () => void | Promise<void>
  onEdit?: (update: TicketUpdate) => void
  replyToId: number | null
  onReplyTo: (id: number | null) => void
  isActivity?: boolean
}

function ThreadComment({
  node,
  depth,
  ticketId,
  currentUserId,
  onRefresh,
  onEdit,
  replyToId,
  onReplyTo,
  isActivity = false,
}: ThreadCommentProps) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [editing, setEditing] = useState(false)
  const [editContent, setEditContent] = useState(node.content)

  const replies = node.replies ?? []
  const name = authorName(node)
  const createdAt = node.createdAt ?? node.created_at
  const userId = node.userId ?? node.user_id
  const canEdit = currentUserId != null && userId === currentUserId
  const canReply = depth < MAX_THREAD_DEPTH - 1
  const showReplyComposer = replyToId === node.id

  const handleSaveEdit = async () => {
    const trimmed = editContent.trim()
    if (!trimmed) return
    const res = await TicketService.editTicketUpdate(ticketId, node.id, { content: trimmed })
    if (res.success) {
      showSuccess('Updated')
      setEditing(false)
      await onRefresh()
    } else {
      showError(res.message || 'Failed to update')
    }
  }

  return (
    <Box role="group" position="relative">
      <Flex
        align="flex-start"
        gap={3}
        pl={depth > 0 ? `${THREAD_INDENT}px` : 0}
        borderLeftWidth={depth > 0 ? '2px' : 0}
        borderLeftColor={colors.border}
        ml={depth > 0 ? 2 : 0}
        py={2}
      >
        <Box
          w="32px"
          h="32px"
          minW="32px"
          borderRadius="full"
          bg={isActivity ? (colors.sectionBg ?? 'gray.100') : 'blue.50'}
          border="1px solid"
          borderColor={isActivity ? colors.borderLight : 'blue.200'}
          display="grid"
          placeItems="center"
          flexShrink={0}
        >
          <Text
            fontWeight="800"
            color={isActivity ? colors.textSecondary : 'blue.600'}
            fontSize="sm"
            lineHeight="1"
          >
            {authorInitial(name)}
          </Text>
        </Box>

        <Box flex="1" minW={0}>
          <HStack spacing={2} flexWrap="wrap" mb={1}>
            <Text fontSize="sm" fontWeight="700" color={colors.text}>
              {name}
            </Text>
            {isActivity && !isCommentType(node) ? (
              <Badge size="sm" colorScheme="gray" fontSize="0.6rem">
                Activity
              </Badge>
            ) : null}
            <Text fontSize="xs" color={colors.textMuted}>
              {formatDate(createdAt)}
            </Text>
          </HStack>

          {editing ? (
            <VStack align="stretch" spacing={2}>
              <Textarea
                value={editContent}
                onChange={(e) => {
                  if (e.target.value.length <= MAX_CHARS) setEditContent(e.target.value)
                }}
                size="sm"
                rows={3}
              />
              <HStack>
                <Button size="xs" variant="ghost" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
                <Button
                  size="xs"
                  colorScheme="blue"
                  onClick={() => void handleSaveEdit()}
                  isDisabled={!editContent.trim()}
                >
                  Save
                </Button>
              </HStack>
            </VStack>
          ) : (
            <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" lineHeight="1.65">
              {node.content}
            </Text>
          )}

          {!editing ? (
            <HStack spacing={3} mt={1.5} opacity={1}>
              {canReply ? (
                <Button
                  size="xs"
                  variant="link"
                  colorScheme="blue"
                  height="auto"
                  minH={0}
                  px={0}
                  leftIcon={<MessageSquare size={12} />}
                  onClick={() => onReplyTo(showReplyComposer ? null : node.id)}
                >
                  Reply
                </Button>
              ) : null}
              {canEdit && onEdit ? (
                <IconButton
                  aria-label="Edit"
                  icon={<Pencil size={12} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => onEdit(node)}
                />
              ) : null}
              {canEdit && !onEdit ? (
                <Button size="xs" variant="link" height="auto" minH={0} px={0} onClick={() => setEditing(true)}>
                  Edit
                </Button>
              ) : null}
            </HStack>
          ) : null}

          {showReplyComposer ? (
            <Box mt={3}>
              <CommentComposer
                ticketId={ticketId}
                parentId={node.id}
                compact
                placeholder={`Reply to ${name}…`}
                submitLabel="Post reply"
                onSubmitted={onRefresh}
                onCancel={() => onReplyTo(null)}
              />
            </Box>
          ) : null}
        </Box>
      </Flex>

      {replies.length > 0 ? (
        <Box position="relative">
          {replies.map((child) => (
            <ThreadComment
              key={child.id}
              node={child}
              depth={depth + 1}
              ticketId={ticketId}
              currentUserId={currentUserId}
              onRefresh={onRefresh}
              onEdit={onEdit}
              replyToId={replyToId}
              onReplyTo={onReplyTo}
              isActivity={isActivity}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  )
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(ms / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  if (days >= 1) return `${days}d ago`
  if (hours >= 1) return `${hours}h ago`
  if (minutes >= 1) return `${minutes}m ago`
  return 'just now'
}

function ActivityFeedRow({ node }: { node: TicketUpdate }) {
  const colors = useThemeColors()
  const createdAt = node.createdAt ?? node.created_at
  const isStatus = node.type === 'status_change'
  const Icon = isStatus ? ArrowRight : Wrench

  return (
    <Flex align="flex-start" gap={3} py={2}>
      <Box
        w="26px"
        h="26px"
        minW="26px"
        borderRadius="full"
        bg={colors.sectionBg ?? 'gray.100'}
        display="grid"
        placeItems="center"
        flexShrink={0}
      >
        <Icon size={13} color={colors.textMuted} aria-hidden />
      </Box>
      <Box flex="1" fontSize="sm" color={colors.textSecondary} pt={0.5}>
        {node.content}
        {createdAt ? (
          <>
            {' '}
            ·{' '}
            <Text as="span" color={colors.textMuted} fontSize="xs">
              {relativeTime(createdAt)}
            </Text>
          </>
        ) : null}
      </Box>
    </Flex>
  )
}

export default function CommentThread({
  ticketId,
  updates,
  onRefresh,
  onEdit,
  embedded = false,
  ...boxProps
}: Props) {
  const colors = useThemeColors()
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)
  const [replyToId, setReplyToId] = useState<number | null>(null)
  const [feedFilter, setFeedFilter] = useState<'all' | 'comments' | 'activity'>('all')

  const handleReplyTo = useCallback((id: number | null) => {
    setReplyToId(id)
  }, [])

  const { commentThreads, activityThreads } = useMemo(
    () => buildCommentTree(updates.map(normalizeUpdate)),
    [updates]
  )

  const unifiedTimeline = useMemo(
    () => buildUnifiedTimeline(updates.map(normalizeUpdate)),
    [updates]
  )

  const visibleTimeline = useMemo(() => {
    if (feedFilter === 'comments') return unifiedTimeline.filter((row) => !row.isActivity)
    if (feedFilter === 'activity') return unifiedTimeline.filter((row) => row.isActivity)
    return unifiedTimeline
  }, [feedFilter, unifiedTimeline])

  const totalNodes = countThreadNodes(commentThreads) + countThreadNodes(activityThreads)
  const hasContent = totalNodes > 0
  const hasVisibleContent = visibleTimeline.length > 0

  return (
    <Box
      display="flex"
      flexDir="column"
      minH={0}
      {...(embedded
        ? { overflow: 'hidden' }
        : {
            bg: colors.cardBg,
            borderRadius: 'lg',
            border: '1px solid',
            borderColor: colors.border,
            overflow: 'hidden',
          })}
      {...boxProps}
    >
      <Flex
        flexShrink={0}
        px={embedded ? 0 : 4}
        py={embedded ? 1.5 : 2}
        borderBottom="1px solid"
        borderColor={colors.borderLight}
        justify="space-between"
        align="center"
        gap={2}
        flexWrap="wrap"
      >
        <HStack spacing={1}>
          {(
            [
              { id: 'all', label: 'All' },
              { id: 'comments', label: 'Comments' },
              { id: 'activity', label: 'System' },
            ] as const
          ).map((item) => (
            <Button
              key={item.id}
              size="xs"
              variant={feedFilter === item.id ? 'solid' : 'ghost'}
              colorScheme={feedFilter === item.id ? 'blue' : 'gray'}
              onClick={() => setFeedFilter(item.id)}
            >
              {item.label}
            </Button>
          ))}
        </HStack>
        {hasContent ? (
          <Text fontSize="xs" color={colors.textMuted}>
            {totalNodes} {totalNodes === 1 ? 'item' : 'items'}
          </Text>
        ) : null}
      </Flex>

      <Box px={embedded ? 0 : 4} py={embedded ? 2 : 3} flex="1" minH={0} overflowY="auto">
        {hasVisibleContent ? (
          <VStack align="stretch" spacing={1} divider={<Box borderColor={colors.borderLight} />}>
            {visibleTimeline.map(({ node, isActivity }) =>
              isActivity ? (
                <ActivityFeedRow key={node.id} node={node} />
              ) : (
                <ThreadComment
                  key={node.id}
                  node={node}
                  depth={0}
                  ticketId={ticketId}
                  currentUserId={currentUserId}
                  onRefresh={onRefresh}
                  onEdit={onEdit}
                  replyToId={replyToId}
                  onReplyTo={handleReplyTo}
                />
              )
            )}
          </VStack>
        ) : (
          <Text fontSize="sm" color={colors.textSecondary} py={2}>
            {hasContent
              ? 'No items in this filter.'
              : 'No activity or comments yet. Be the first to post.'}
          </Text>
        )}
      </Box>
    </Box>
  )
}
